import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle, Mail } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-email-unsubscribe`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State = "loading" | "valid" | "invalid" | "already" | "done" | "error";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return setState("invalid");
    (async () => {
      try {
        const res = await fetch(`${FN_URL}?token=${encodeURIComponent(token)}`, {
          headers: { apikey: ANON_KEY },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.valid) setState("valid");
        else if (data?.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      } catch {
        setState("error");
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY },
        body: JSON.stringify({ token }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) setState("done");
      else if (data?.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md rounded-2xl">
        <CardContent className="p-8 text-center">
          <Link to="/" className="inline-flex justify-center mb-6">
            <WeKitLogo className="h-8 w-8" />
          </Link>

          {state === "loading" && (
            <>
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
              <p className="mt-4 text-sm text-muted-foreground">Checking your link…</p>
            </>
          )}

          {state === "valid" && (
            <>
              <Mail className="h-10 w-10 mx-auto text-primary" />
              <h1 className="mt-4 font-display text-2xl font-bold">Unsubscribe?</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                You'll stop receiving emails from WeKIT™ Career Clarity 360.
              </p>
              <Button
                onClick={confirm}
                disabled={busy}
                className="mt-6 w-full rounded-full"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm unsubscribe"}
              </Button>
            </>
          )}

          {state === "done" && (
            <>
              <CheckCircle2 className="h-10 w-10 mx-auto text-primary" />
              <h1 className="mt-4 font-display text-2xl font-bold">You're unsubscribed</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                You won't receive further emails from us.
              </p>
            </>
          )}

          {state === "already" && (
            <>
              <CheckCircle2 className="h-10 w-10 mx-auto text-primary" />
              <h1 className="mt-4 font-display text-2xl font-bold">Already unsubscribed</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This email address is no longer on our list.
              </p>
            </>
          )}

          {(state === "invalid" || state === "error") && (
            <>
              <XCircle className="h-10 w-10 mx-auto text-destructive" />
              <h1 className="mt-4 font-display text-2xl font-bold">Link not valid</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This unsubscribe link is invalid or has expired.
              </p>
            </>
          )}

          <Button asChild variant="ghost" className="mt-6 rounded-full text-xs">
            <Link to="/">Back to wekit.ai</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
